import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';

import { scrubTelemetryItem } from './scrubTelemetryItem';

export const reactPlugin = new ReactPlugin();

const createAppInsights = (): ApplicationInsights | null => {
  const connectionString = (import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING ?? '').trim();
  // loadAppInsights throws without an instrumentation key; an uninitialized reactPlugin is a safe no-op.
  if (!connectionString) {
    return null;
  }

  try {
    const instance = new ApplicationInsights({
      config: {
        connectionString,
        extensions: [reactPlugin],
        enableAutoRouteTracking: true,
      },
    });
    // Before loading, so the SDK never runs without the scrubber.
    instance.addTelemetryInitializer(scrubTelemetryItem);
    instance.loadAppInsights();
    return instance;
  } catch (error) {
    // The module loads at app start, so a malformed connection string must turn telemetry off, not blank the portal.
    console.error('Application Insights is off: the SDK rejected the connection string.', error);
    return null;
  }
};

/** The browser SDK, or null when this build has no usable connection string. */
export const appInsights: ApplicationInsights | null = createAppInsights();

/** Tags telemetry with the signed-in user's opaque id, or clears it for null. */
export function setTelemetryUser(userId: string | null): void {
  if (userId) {
    // No cookie, so the id cannot outlive a logout.
    appInsights?.setAuthenticatedUserContext(userId, undefined, false);
  } else {
    appInsights?.clearAuthenticatedUserContext();
  }
}

/** The browser session id support searches by, or null without the SDK. */
export function getTelemetrySessionId(): string | null {
  // Typed string, but null until the SDK tracks its first item.
  return appInsights?.context?.getSessionId() || null;
}
